import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback } from 'react'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations'
import { useSafetyTranslation } from 'cng-common/features/translations/hooks'
import { ATTENTION_STATUS_ACTIONS } from 'partner360/entities/attentionStatuses'
import {
  ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS,
  useAcknowledgmentPendingSigners,
} from 'partner360/features/acknowledgmentStatusDialog'
import { getTranslatedText } from 'partner360/helpers/getTranslatedText'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import { invalidateFinancialCockpitData } from '../../../../model/financialCockpitSlice'
import { useNextIncompleteOffering } from '../../../../model/hooks/useNextIncompleteOffering'
import { selectCockpitPartnerData, selectEffectiveCockpitFYear } from '../../../../model/selectors'
import type { IAttentionStatusesItem } from '../../types'

import { AttentionNextStepRow } from './AttentionNextStepRow'
import { AttentionNextStepsGroup } from './AttentionNextStepsGroup'
import { NEXT_STEPS_LIMIT } from './constants'
import { getStepInitials } from './getStepInitials'
import { usePlanSignFlow } from './usePlanSignFlow'

import styles from './AttentionNextSteps.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses.nextSteps

interface IAcknowledgmentNextStepsProps {
  item: IAttentionStatusesItem
  onSelect: (id: string) => void
}

/**
 * `Offering X is not fully acknowledged`: the signers the plan is waiting for next, as the server
 * marks them (`SignoffPending`). The reader's own row signs; every other row reminds its signer,
 * once a day, as the acknowledgment window's `Notify` does. A row the reader may do neither on
 * opens the status detail, as `Details` does, so a step never names a task without a way to it.
 *
 * The signers are not part of the cockpit's payload, so they are read for this status alone, the
 * way the acknowledgment window reads them.
 */
export const AcknowledgmentNextSteps = ({ item, onSelect }: IAcknowledgmentNextStepsProps) => {
  const t = useSafetyTranslation()
  const dispatch = useAppDispatch()
  const partnerData = useSelector(selectCockpitPartnerData)
  const fYear = useSelector(selectEffectiveCockpitFYear)
  const partnerId = partnerData[0]?.PartnerId ?? ''
  const offering = item.offering ?? null

  const handleUpdated = useCallback(() => {
    dispatch(invalidateFinancialCockpitData())
  }, [dispatch])

  const { signers, hasLoaded, isOwnRow, isReminded, remind, isSubmitting } =
    useAcknowledgmentPendingSigners({ partnerId, fYear, offering, onUpdated: handleUpdated })

  const signFlow = usePlanSignFlow({ partnerId, fYear, offering })
  const shown = signers.slice(0, NEXT_STEPS_LIMIT)
  /* Every row here is one the server marks `SignoffPending`, so the reader's own row signs. Another
     signer's row reminds them where the server lets the reader, as the window's `Notify` does; a
     reminder the server holds (`-1`) until another plan is complete says why and leads to that
     plan, and a row allowing neither opens the detail. */
  const getRowAct = (signer: (typeof signers)[number]) => {
    if (isOwnRow(signer)) {
      return 'sign'
    }

    if (signer.CanResend > 0) {
      return 'remind'
    }

    return signer.CanResend < 0 ? 'blocked' : 'detail'
  }

  const nextOffering = useNextIncompleteOffering(offering)
  const nextOfferingParams = nextOffering
    ? [{ key: '{Offering}', value: getTranslatedText({ t, key: nextOffering.titleKey }) }]
    : undefined
  const hasDetailStep = hasLoaded && shown.some((signer) => getRowAct(signer) === 'detail')

  /* Nothing is said until the signers are read, so the line does not first claim there is
     nothing to do. */
  const steps = !hasLoaded ? undefined : shown.length === 0 ? null : (
    <>
      {shown.map((signer) => {
        const isOwn = isOwnRow(signer)
        const name = signer.SignerName?.trim() || signer.SignerEmail || ''
        const act = getRowAct(signer)
        const badge = isOwn ? (
          <span className={styles.you}>
            <Translation span text={ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.common.you} />
          </span>
        ) : undefined

        if (act === 'blocked') {
          return (
            <AttentionNextStepRow
              key={signer.EntryId}
              mark={{ kind: 'person', text: getStepInitials(name) }}
              name={name}
              fact={signer.AlertKey ? <Translation span text={signer.AlertKey} /> : undefined}
              stacked
              tone={nextOffering ? 'go' : 'remind'}
              severity={item.severity}
              labelKey={nextOffering ? KEYS.openOffering : KEYS.remind}
              labelParams={nextOfferingParams}
              disabled={!nextOffering}
              onClick={() => nextOffering?.open()}
            />
          )
        }

        const canSign = act === 'sign'
        const canRemind = act === 'remind'
        const reminded = canRemind && isReminded(signer)
        const tone = canSign ? 'sign' : canRemind ? 'remind' : 'go'
        const labelKey = canSign
          ? ATTENTION_STATUS_ACTIONS.acknowledge.labelKey
          : canRemind
            ? reminded
              ? KEYS.remindedToday
              : KEYS.remind
            : KEYS.seeDetails
        const handleClick = canSign
          ? () => signFlow.start(signer.EntryId)
          : canRemind
            ? () => void remind(signer)
            : () => onSelect(item.id)

        return (
          <AttentionNextStepRow
            key={signer.EntryId}
            mark={{ kind: 'person', text: getStepInitials(name), isOwn }}
            name={name}
            badge={badge}
            fact={[signer.CommitmentTypeName, signer.SignerRole].filter(Boolean).join(' · ')}
            tone={tone}
            severity={item.severity}
            labelKey={labelKey}
            loading={canSign ? signFlow.isSigning : false}
            disabled={canRemind && (reminded || isSubmitting)}
            onClick={handleClick}
          />
        )
      })}
      {signFlow.element}
    </>
  )

  return (
    <AttentionNextStepsGroup
      item={item}
      onSelect={onSelect}
      restCount={signers.length - shown.length}
      hasDetailStep={hasDetailStep}
    >
      {steps}
    </AttentionNextStepsGroup>
  )
}

AcknowledgmentNextSteps.displayName = 'AcknowledgmentNextSteps'
